// ============================================
// Client Dashboard
// ============================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../utils/api';
import { Loader } from '../components/Loader';
import { cn } from '../components/ui';
import { AlertTriangle, CheckCircle2, Plus, Users } from 'lucide-react';
import type { DashboardData } from '../types';

function getErrorMessage(error: any): string {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg).filter(Boolean).join('. ') || 'Invalid dashboard request';
  }
  return 'Failed to load dashboard';
}

function CredentialStatus({ label, configured }: { label: string; configured: boolean }) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
      configured
        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
    )}>
      {configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
      {label}: {configured ? 'Ready' : 'Missing'}
    </span>
  );
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardApi.getOverview();
      setData(response.data);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader fullScreen />;

  if (error) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-500" />
        <h2 className="mb-2 text-xl font-semibold">Couldn’t load your clients</h2>
        <p className="mb-5 text-muted-foreground">{error}</p>
        <button onClick={() => void loadDashboard()} className="rounded-lg bg-primary px-4 py-2 text-primary-foreground">
          Try again
        </button>
      </div>
    );
  }

  const clients = data?.sub_accounts ?? [];
  const ghlReady = clients.filter((client) => client.has_ghl_credentials).length;
  const activeClients = clients.filter((client) => client.is_active).length;
  const metrics = [
    { label: 'Clients', value: data?.total_sub_accounts ?? 0, detail: 'managed accounts', icon: Users, tone: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400' },
    { label: 'GHL connected', value: ghlReady, detail: `of ${clients.length} clients`, icon: CheckCircle2, tone: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400' },
    { label: 'Active clients', value: activeClients, detail: 'enabled accounts', icon: Users, tone: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400' },
  ];

  return (
    <div className="space-y-7">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">Agency workspace</p>
          <h1 className="text-3xl font-bold tracking-tight">Client accounts</h1>
          <p className="mt-1 text-muted-foreground">Manage client accounts and GoHighLevel credentials.</p>
        </div>
        <Link to="/sub-accounts/new" className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 font-medium text-primary-foreground hover:bg-primary/90">
          <Plus className="h-4 w-4" /> Add client
        </Link>
      </header>

      <section aria-label="Client connection summary" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map(({ label, value, detail, icon: Icon, tone }) => (
          <article key={label} className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{label}</p>
                <p className="mt-2 text-3xl font-semibold tabular-nums">{value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
              </div>
              <span className={cn('rounded-lg p-3', tone)}><Icon className="h-5 w-5" /></span>
            </div>
          </article>
        ))}
      </section>

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="font-semibold">Your clients</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">Credential readiness is shown per client.</p>
          </div>
          <span className="text-sm text-muted-foreground">{clients.length} total</span>
        </div>

        {clients.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Users className="mx-auto mb-4 h-11 w-11 text-muted-foreground" />
            <h3 className="text-lg font-medium">No clients added yet</h3>
            <p className="mx-auto mb-5 mt-1 max-w-md text-sm text-muted-foreground">Add a client to store business details and connect its GHL credentials.</p>
            <Link to="/sub-accounts/new" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90">
              <Plus className="h-4 w-4" /> Add your first client
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px]">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Client</th>
                                    <th className="px-6 py-3 font-medium">GoHighLevel</th>
                  <th className="px-6 py-3 font-medium">Account</th>
                  <th className="px-6 py-3 text-right font-medium">Manage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {clients.map((client) => (
                  <tr key={client.id} className="transition-colors hover:bg-muted/30">
                    <td className="px-6 py-4">
                      <Link to={`/sub-account/${client.id}`} className="font-medium hover:text-primary">{client.name}</Link>
                      <p className="mt-0.5 text-xs text-muted-foreground">/{client.slug}</p>
                    </td>
                                        <td className="px-6 py-4"><CredentialStatus label="GHL" configured={client.has_ghl_credentials} /></td>
                    <td className="px-6 py-4">
                      <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-medium', client.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300')}>
                        {client.is_active ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/sub-account/${client.id}`} className="text-sm font-medium text-primary hover:underline">Open account</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
